import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import SmoothScrollbar from 'smooth-scrollbar';
import { ScrollStatus } from 'smooth-scrollbar/interfaces';
import { fadeAnimation } from 'src/app/animations/fade-animation';
import { LANG } from 'src/app/models/enums/lang.enum';
import { LocalStorageEnum } from 'src/app/models/enums/local-storage.enum';
import { EXTENDED_ROUTE_PMS } from 'src/app/models/enums/route-permission';
import { HeaderTitle } from 'src/app/models/header-title';
import { User } from 'src/app/models/user';
import { AuthService } from 'src/app/services/auth.service';
import { DashboardService } from 'src/app/services/dashboard.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { RequestService } from 'src/app/services/request.service';
import { SingleSignOnService } from 'src/app/services/single-sign-on.service';
import { UtilService } from 'src/app/services/util.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  animations: [fadeAnimation],
})
export class NavigationComponent implements OnInit, OnDestroy {
  isTablet!: boolean;
  isHandset!: boolean;

  NSSF_AUTH_URL: string = environment.NSSF_AUTH_URL;
  IS_ENABLE_OAUTH: boolean = environment.IS_ENABLE_OAUTH;
  APP_URL: string = environment.app_url;
  version: string = environment.VERSION;
  IS_SYSTEM_TESTING: boolean = environment.IS_SYSTEM_TESTING;

  isContentScrolled!: boolean;
  scrollToTop: boolean = false;

  displayMenuItems: MenuItem[] = [];

  isScrolled!: boolean;
  isMenuIcon: boolean = false;
  menuState: 'open' | 'close' = 'close';
  user!: User;
  lang: string = '';
  permissions: string[] = [];
  title: HeaderTitle | null = null;
  showActionButton: boolean = false;
  readonly LANG = LANG;

  firstHospital: string | null = null;
  userInfoUsername: string = '';
  userInfoRoleName: string = '';

  //#region Count
  expire_soon_count: number = 0;
  license_expire_soon_count: number = 0;
  hospital_submitted_count: number = 0;
  return_claim_count: number = 0;
  pharmacy_submitted_count: number = 0;

  private _destroyed = new Subject<void>();

  constructor(
    private _router: Router,
    public util: UtilService,
    private _cd: ChangeDetectorRef,
    private authService: AuthService,
    private translate: TranslateService,
    private requestService: RequestService,
    private dashboardService: DashboardService,
    private breakpointObserver: BreakpointObserver,
    private singleSignOnService: SingleSignOnService,
    private localStorageService: LocalStorageService,
  ) {
    this.lang = this.translate.currentLang as LANG;

    this.authService
      .getUserInfo()
      .pipe(takeUntil(this._destroyed))
      .subscribe((res: any) => {
        if (res?.data?.roles?.length! > 0) {
          const permission = res.data.roles.flatMap((role: any) =>
            Array.isArray(role.permissions)
              ? role.permissions
              : [role.permissions],
          );

          const code = Array.from(new Set(permission)) as string[];

          this.localStorageService.setArray(LocalStorageEnum.permissions, code);

          this.permissions = code;
        } else {
          this.permissions = [];
          this.localStorageService.setArray(LocalStorageEnum.permissions, []);
        }

        // Store user info for profile dropdown
        if (res?.data) {
          this.userInfoUsername = res.data.username || '';
          this.userInfoRoleName = res.data.roles?.[0]?.name_en || '';
        }

        this.getMenuItems();
        this._updateActive(this._router.url.slice(1).split('/').shift()!);
        this._cd.detectChanges();
      });

    let value: any = this.localStorageService.decryptSpecialCharacter(
      LocalStorageEnum.user,
    );

    this.user = value ? JSON.parse(value) : '';

    this.util.showActionButton
      .pipe(takeUntil(this._destroyed))
      .subscribe((res) => {
        this.showActionButton = !!res;
      });

    this.permissions = localStorageService.getArray(
      LocalStorageEnum.permissions,
    );

    // Auto logout
    if (this.IS_ENABLE_OAUTH && !this.singleSignOnService.isLoggedIn) {
      this.authService.logout().subscribe((res: any) => {
        if (res.status === 1) {
          this.authService.logOutEventEmitter.emit(true);
          // this.requestService.onSignOut();
        }
      });
    }

    // Listen for external menu collapse/expand requests
    this.util.menuChange
      .pipe(takeUntil(this._destroyed))
      .subscribe((collapsed: boolean) => {
        this.isMenuIcon = collapsed;
        this._cd.detectChanges();
      });
  }

  private _updateActive(root: string) {
    const active = this.displayMenuItems.find((menu) => menu.root === root);
    if (Boolean(active)) {
      if (!active!.expanded) {
        active!.expanded = true;
        this._cd.detectChanges();
      }
    }
  }

  ngOnInit(): void {
    this._updateActive(this._router.url.slice(1).split('/').shift()!);
    this._router.events.pipe(takeUntil(this._destroyed)).subscribe((ev) => {
      if (ev instanceof NavigationEnd) {
        const root = ev.url.slice(1).split('/').shift()!;
        this._updateActive(root);
      }
    });

    this.util.headerTitle.subscribe((title) => {
      this.title = title;
    });

    this.breakpointObserver
      .observe(Breakpoints.Handset)
      .pipe(takeUntil(this._destroyed))
      .subscribe((response) => {
        this.isHandset = response.matches;
        this.isMenuIcon = response.matches;
      });

    this.breakpointObserver
      .observe(Breakpoints.TabletPortrait)
      .pipe(takeUntil(this._destroyed))
      .subscribe((response) => {
        this.isTablet = response.matches;
        this.isMenuIcon = response.matches;
      });

    this.translate.onLangChange
      .pipe(takeUntil(this._destroyed))
      .subscribe((event: LangChangeEvent) => {
        this.lang = event.lang as LANG;
      });

    if (this.user?.manage_hospital?.length > 0) {
      const hospital = this.user.manage_hospital[0];
      this.firstHospital = hospital;
      this._cd.detectChanges();
    }

    this.getMenuCount();
  }

  private getMenuCount() {
    this.dashboardService.getCount({}).subscribe((res) => {
      if (!res) {
        this.expire_soon_count = 0;
        this.return_claim_count = 0;
        this.hospital_submitted_count = 0;
        this.pharmacy_submitted_count = 0;
        this.license_expire_soon_count = 0;
        return;
      }

      this.expire_soon_count = res.data?.expire_soon_count || 0;
      this.return_claim_count = res.data?.return_claim_count || 0;
      this.hospital_submitted_count = res.data?.hospital_submitted_count || 0;
      this.pharmacy_submitted_count = res.data?.pharmacy_submitted_count || 0;
      this.license_expire_soon_count = res.data?.license_expire_soon_count || 0;

      this.getMenuItems();
      this._cd.markForCheck();
      this._cd.detectChanges();
    });
  }

  private traverseMenuItems(menuItems: MenuItem[]) {
    return menuItems.filter((item) => {
      if (item.children) {
        item.children = this.traverseMenuItems(item.children);
      }

      // if (!item.roles || !this.user?.role?.code) {
      //   return true;
      // }

      // return item.roles.includes(this.user.role.code);
      return true; //show all menus
    });
  }

  getMenuItems() {
    // Template for checking permission (not available yet)
    // ------
    // isNoPermission: [...EXTENDED_ROUTE_PMS.REQUEST_FORM, ...EXTENDED_ROUTE_PMS.REQUEST_LIST].filter(p => this.permissions?.includes(p)).length === 0,

    const menuItems = [
      {
        title: 'menu.dashboard',
        routerLink: '/dashboard',
        icon: 'dashboard',
        isNoPermission:
          EXTENDED_ROUTE_PMS.DASHBOARD.filter((p) =>
            this.permissions?.includes(p),
          ).length === 0,
        roles: [],
      },
      {
        title: 'menu.hospital',
        routerLink: '/register-hospital',
        icon: 'home_health',
        isNoPermission:
          EXTENDED_ROUTE_PMS.REGISTER_HOSPITAL.filter((p) =>
            this.permissions?.includes(p),
          ).length === 0,
        roles: [],
      },
      {
        title: 'menu.member_ship',
        routerLink: '/beneficiary',
        icon: 'menu_book',
        isNoPermission:
          EXTENDED_ROUTE_PMS.BENEFICIARY.filter((p) =>
            this.permissions?.includes(p),
          ).length === 0,
        roles: [],
      },
      {
        title: 'menu.all_member_ship',
        routerLink: '/beneficiary-list',
        icon: 'menu_book',
        isNoPermission:
          EXTENDED_ROUTE_PMS.BENEFICIARY_ALL_READ.filter((p) =>
            this.permissions?.includes(p),
          ).length === 0,
        roles: [],
      },
      {
        title: 'menu._claim_request.title',
        icon: 'medication',
        root: 'claim-request',
        isNoPermission:
          [
            ...EXTENDED_ROUTE_PMS.CONSUMPTION,
            ...EXTENDED_ROUTE_PMS.CLAIM_REQUEST_HOS,
            ...EXTENDED_ROUTE_PMS.PAYMENT_REQUEST,
            ...EXTENDED_ROUTE_PMS.TRANSMITTAL_REQUEST,
            ...EXTENDED_ROUTE_PMS.PATIENT_PRINT_FORM,
          ].filter((p) => this.permissions?.includes(p)).length === 0,
        roles: [],
        children: [
          {
            title: 'menu.patient_form',
            routerLink: '/claim-request/patient-form',
            icon: 'print',
            isNoPermission:
              EXTENDED_ROUTE_PMS.PATIENT_PRINT_FORM.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'menu.consumption.title',
            routerLink: '/claim-request/consumption',
            icon: 'playlist_add_check_circle',
            isNoPermission:
              EXTENDED_ROUTE_PMS.CONSUMPTION.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          // {
          //   title: 'menu._claim_request.request',
          //   routerLink: '/claim-request/claim-request',
          //   icon: 'assignment_turned_in',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.CLAIM_REQUEST.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          {
            title: 'menu._claim_request.request',
            routerLink: '/claim-request/claim-request',
            icon: 'assignment_turned_in',
            isNoPermission:
              EXTENDED_ROUTE_PMS.CLAIM_REQUEST_HOS.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            count: this.return_claim_count,
          },
          // {
          //   title: 'menu._claim_request.req_note',
          //   routerLink: '/claim-request/request-note',
          //   icon: 'fact_check',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.PAYMENT_REQUEST.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'menu._claim_request.transmittal_letter',
          //   routerLink: '/claim-request/transmittal-letter',
          //   icon: 'receipt_long',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.TRANSMITTAL_REQUEST.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          //   isMenuLong: true,
          // },
          // {
          //   title: 'menu._claim_request.post_process',
          //   routerLink: '/claim-request/post-process-claim-request',
          //   icon: 'assignment_turned_in',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.POST_PROCESSING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          //   isMenuLong: true,
          // },
        ],
      },
      {
        title: 'menu.payment_claim.title',
        icon: 'currency_exchange',
        root: 'payment-claim',
        isNoPermission:
          [
            ...EXTENDED_ROUTE_PMS.CLAIM_REQUEST_NSSF,
            ...EXTENDED_ROUTE_PMS.TRANSMITTAL_REQUEST,
            ...EXTENDED_ROUTE_PMS.PAYMENT_REQUEST,
          ].filter((p) => this.permissions?.includes(p)).length === 0,
        roles: [],
        children: [
          {
            title: 'menu.payment_claim.provider_claim',
            routerLink: '/payment-claim/claim-request',
            icon: 'assignment_turned_in',
            isNoPermission:
              EXTENDED_ROUTE_PMS.CLAIM_REQUEST_NSSF.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'menu._claim_request.req_note',
            routerLink: '/payment-claim/request-note',
            icon: 'fact_check',
            isNoPermission:
              EXTENDED_ROUTE_PMS.PAYMENT_REQUEST.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'menu._claim_request.transmittal_letter',
            routerLink: '/payment-claim/transmittal-letter',
            icon: 'receipt_long',
            isNoPermission:
              EXTENDED_ROUTE_PMS.TRANSMITTAL_REQUEST.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            isMenuLong: false,
          },
        ],
      },
      {
        title: 'menu.hospital',
        icon: 'local_hospital',
        root: 'hospital',
        isNoPermission:
          [
            ...EXTENDED_ROUTE_PMS.HOSPITAL,
            ...EXTENDED_ROUTE_PMS.HOSPITAL_REQUEST,
            ...EXTENDED_ROUTE_PMS.HOSPITAL_CONTRACT,
            ...EXTENDED_ROUTE_PMS.HOSPITAL_USER,
          ].filter((p) => this.permissions?.includes(p)).length === 0,
        roles: [],
        children: [
          {
            title: 'setting.hospital.hospital_request',
            routerLink: '/hospital/hospital-request',
            icon: 'app_registration',
            isNoPermission:
              EXTENDED_ROUTE_PMS.HOSPITAL_REQUEST.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            count: this.hospital_submitted_count,
          },
          {
            title: 'setting.hospital.hospital_result',
            routerLink: '/hospital/hospital-result',
            icon: 'menu_book',
            isNoPermission:
              EXTENDED_ROUTE_PMS.HOSPITAL.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            count: this.license_expire_soon_count,
          },
          {
            title: 'menu._hospital.hos_contract',
            routerLink: '/hospital/hospital-contract',
            icon: 'description',
            isNoPermission:
              EXTENDED_ROUTE_PMS.HOSPITAL_CONTRACT.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          // {
          //   title: 'menu._hospital.hos_noncontract',
          //   routerLink: '/hospital/hospital-noncontract',
          //   icon: 'remove_circle',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.HOSPITAL_NON_CONTRACT.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          {
            title: 'menu._hospital.hos_manager',
            routerLink: '/hospital/hospital-manager',
            icon: 'manage_accounts',
            isNoPermission:
              EXTENDED_ROUTE_PMS.HOSPITAL_USER.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
        ],
      },
      {
        title: 'setting.pharmacy.title',
        icon: 'medication',
        root: 'pharmacy',
        isNoPermission:
          [
            ...EXTENDED_ROUTE_PMS.PHARMACY,
            ...EXTENDED_ROUTE_PMS.PHARMACY_REQUEST,
            ...EXTENDED_ROUTE_PMS.PHARMACY_CONTRACT,
            ...EXTENDED_ROUTE_PMS.PHARMACY_USER,
          ].filter((p) => this.permissions?.includes(p)).length === 0,
        roles: [],
        children: [
          {
            title: 'setting.pharmacy.request',
            routerLink: '/pharmacy/pharmacy-request',
            icon: 'post_add',
            isNoPermission:
              EXTENDED_ROUTE_PMS.PHARMACY_REQUEST.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            count: this.pharmacy_submitted_count,
          },
          {
            title: 'setting.pharmacy.result',
            routerLink: '/pharmacy/pharmacies',
            icon: 'menu_book',
            isNoPermission:
              EXTENDED_ROUTE_PMS.PHARMACY.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'menu._pharmacy.phar_contract',
            routerLink: '/pharmacy/pharmacy-contract',
            icon: 'description',
            isNoPermission:
              EXTENDED_ROUTE_PMS.PHARMACY_CONTRACT.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'menu._pharmacy.pharmacy_manager',
            routerLink: '/pharmacy/pharmacy-manager',
            icon: 'group',
            isNoPermission:
              EXTENDED_ROUTE_PMS.PHARMACY_USER.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
        ],
      },
      // {
      //   title: 'setting.automatic_approval.title',
      //   icon: 'verified_user',
      //   root: 'approval',
      //   // isNoPermission:
      //   //   [...EXTENDED_ROUTE_PMS.AUTO_APPROVAL_CONFIGS].filter((p) =>
      //   //     this.permissions?.includes(p)
      //   //   ).length === 0,
      //   roles: [],
      //   children: [
      //     {
      //       title: 'setting.automatic_approval.approval_config',
      //       routerLink: '/auto-approvals/auto-approval-config',
      //       icon: 'library_add_check',
      //       // isNoPermission:
      //       //   EXTENDED_ROUTE_PMS.AUTO_APPROVAL_CONFIGS.filter((p) =>
      //       //     this.permissions?.includes(p)
      //       //   ).length === 0,
      //       roles: [],
      //     },
      //     {
      //       title: 'setting.automatic_approval.approval_audit_log',
      //       routerLink: '/auto-approvals/auto-approval-audit-logs',
      //       icon: 'library_add_check',
      //       // isNoPermission:
      //       //   EXTENDED_ROUTE_PMS.AUTO_APPROVAL_AUDITLOGS.filter((p) =>
      //       //     this.permissions?.includes(p)
      //       //   ).length === 0,
      //       roles: [],
      //     },
      //   ],
      // },
      // {
      // 	title: "menu.hospital_payment",
      // 	routerLink: '/hospital-payment',
      // 	icon: 'difference',
      //   // isNoPermission: EXTENDED_ROUTE_PMS.DASHBOARD.filter((p) =>this.permissions?.includes(p)).length === 0,
      // 	roles: []
      // },
      // {
      // 	title: "menu.claim_non_hf.title",
      // 	routerLink: '/claim-non-contract-hf',
      // 	icon: 'request_quote',
      //   // isNoPermission: EXTENDED_ROUTE_PMS.DASHBOARD.filter((p) =>this.permissions?.includes(p)).length === 0,
      // 	roles: []
      // },

      {
        title: 'menu._report.title',
        icon: 'report',
        roles: [],
        root: 'report',
        isNoPermission:
          EXTENDED_ROUTE_PMS.ALL_REPORT.filter((p) =>
            this.permissions?.includes(p),
          ).length === 0,
        children: [
          {
            title: 'menu.member_ship',
            routerLink: '/report/print-patient',
            icon: 'fingerprint',
            isNoPermission:
              EXTENDED_ROUTE_PMS.READ_REPORT_PATIENT_FORM.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
          },
          {
            title: 'menu.consumption.title',
            routerLink: '/report/consumption',
            icon: 'article_shortcut',
            isNoPermission:
              EXTENDED_ROUTE_PMS.CONSUMPTION_REPORT.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
          },
          {
            title: 'menu.beneficiary_type',
            routerLink: '/report/ben-type',
            icon: 'devices_fold_2',
            isNoPermission:
              EXTENDED_ROUTE_PMS.READ_REPORT_BENEFICIARY.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
          },
          {
            title: 'menu._report.payment',
            routerLink: '/report/payment',
            icon: 'currency_exchange',
            isNoPermission:
              EXTENDED_ROUTE_PMS.REPORT_HF_PAYMENT.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
          },
          {
            title: 'general.hospital.title',
            routerLink: '/report/hospital',
            icon: 'personal_injury',
            isNoPermission:
              EXTENDED_ROUTE_PMS.HOSPITAL_REPORT.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
          },
        ],
      },
      // {
      //   title: 'menu.user_admin',
      //   routerLink: '/user-admin',
      //   icon: 'manage_accounts',
      //   isNoPermission:
      //     EXTENDED_ROUTE_PMS.NSSF_USER.filter((p) =>
      //       this.permissions?.includes(p),
      //     ).length === 0,
      //   roles: [],
      // },
      {
        title: 'menu.user_admin',
        routerLink: '/user-account',
        icon: 'manage_accounts',
        isNoPermission:
          EXTENDED_ROUTE_PMS.NSSF_USER.filter((p) =>
            this.permissions?.includes(p),
          ).length === 0,
        roles: [],
      },
      {
        title: 'menu.setting',
        icon: 'settings',
        isNoPermission:
          [
            ...EXTENDED_ROUTE_PMS.SETTING,
            ...EXTENDED_ROUTE_PMS.HOSPITAL_SERVICE,
            ...EXTENDED_ROUTE_PMS.HOSPITAL_CONTRACT_CASE,
          ].filter((p) => this.permissions?.includes(p)).length === 0,
        root: 'setting',
        roles: [],
        expanded: false,
        children: [
          {
            title: 'menu.role',
            routerLink: 'setting/role',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            icon: 'admin_panel_settings',
            roles: [],
          },
          {
            title: 'menu.role_hos',
            routerLink: 'setting/role-hospital',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            icon: 'health_and_safety',
            roles: [],
          },
          {
            title: 'setting.case_category',
            routerLink: 'setting/lumpsum-service',
            icon: 'paid',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'setting.hos_contract_case',
            routerLink: 'setting/contract-casebase-type',
            icon: 'paid',
            isNoPermission:
              EXTENDED_ROUTE_PMS.HOSPITAL_CONTRACT_CASE.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            isMenuLong: false,
          },
          {
            title: 'menu.template_casebase',
            routerLink: 'setting/template-casebase',
            icon: 'cases',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            isMenuLong: false,
          },
          {
            title: 'setting.service',
            routerLink: 'setting/service',
            icon: 'content_paste_search',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'setting.hos_contract',
            routerLink: 'setting/hospital-service',
            icon: 'person',
            isNoPermission:
              EXTENDED_ROUTE_PMS.HOSPITAL_SERVICE.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'setting.tu_hospital_wards',
            routerLink: 'setting/hospital-wards',
            icon: 'hotel',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          // {
          //   title: 'setting.case_type',
          //   routerLink: 'setting/lumpsum-type',
          //   icon: 'paid',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.coverage_type',
          //   routerLink: 'setting/coverage-type',
          //   icon: 'local_shipping',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.covered_distance',
          //   routerLink: 'setting/covered-distance',
          //   icon: 'pin_drop',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          {
            title: 'setting.diagnosis',
            routerLink: 'setting/diagnosis',
            icon: 'local_hospital',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'setting.diagnosis_type',
            routerLink: 'setting/diagnosis-type',
            icon: 'local_hospital',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          // {
          //   title: 'setting.disease_type',
          //   routerLink: 'setting/disease-type',
          //   icon: 'local_hospital',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p),
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.hospital_type',
          //   routerLink: 'setting/hospital-type',
          //   icon: 'local_hospital',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.hospital_level',
          //   routerLink: 'setting/hospital-level',
          //   icon: 'signal_cellular_alt',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.hospital_package_level',
          //   routerLink: 'setting/hospital-package-level',
          //   icon: 'medical_services',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.hospital_payment_type',
          //   routerLink: 'setting/hospital-payment-type',
          //   icon: 'checklist_rtl',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.hospital_drug',
          //   routerLink: 'setting/hospital-drug',
          //   icon: 'local_pharmacy',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p),
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.non_contract_casebase',
          //   routerLink: 'setting/non-contract-casebase',
          //   icon: 'paid',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          //   isMenuLong:true
          // },
          {
            title: 'setting.medicine.group',
            routerLink: 'setting/medicine-group',
            icon: 'science',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'setting.medicine.info',
            routerLink: 'setting/medicine-info',
            icon: 'vaccines',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          {
            title: 'setting.drug_price',
            routerLink: 'setting/medicine-price',
            icon: 'price_change',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          // {
          //   title: 'setting.medical_hospital_reason',
          //   routerLink: 'setting/medical-hospital-reason',
          //   icon: 'info',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p),
          //     ).length === 0,
          //   roles: [],
          // },
          {
            title: 'setting.operation_district',
            routerLink: 'setting/operation-distric',
            icon: 'location_on',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          // {
          //   title: 'setting.organization',
          //   routerLink: 'setting/organization',
          //   icon: 'business',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p),
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.organization_category',
          //   routerLink: 'setting/organization-category',
          //   icon: 'checklist_rtl',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p),
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.print_type',
          //   routerLink: 'setting/print-type',
          //   icon: 'checklist_rtl',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.referrer_type',
          //   routerLink: 'setting/referrer-type',
          //   icon: 'description',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.category_visit',
          //   routerLink: 'setting/category-visit',
          //   icon: 'checklist_rtl',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          {
            title: 'setting.sub_category_visit',
            routerLink: 'setting/sub-category-visit',
            icon: 'checklist_rtl',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
          // {
          //   title: 'setting.transport_sevice',
          //   routerLink: 'setting/transport-service',
          //   icon: 'currency_exchange',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          {
            title: 'setting.tu_non_contract_hospital_services',
            routerLink: 'setting/non-contract-hospital-services',
            icon: 'sync_problem',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
            isMenuLong: true,
          },
          // {
          //   title: 'setting.visit_type',
          //   routerLink: 'setting/visit-type',
          //   icon: 'health_and_safety',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.doctors',
          //   routerLink: 'setting/doctors',
          //   icon: 'medical_services',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          // {
          //   title: 'setting.doctor_position',
          //   routerLink: 'setting/doctor-position',
          //   icon: 'person',
          //   isNoPermission:
          //     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
          //       this.permissions?.includes(p)
          //     ).length === 0,
          //   roles: [],
          // },
          {
            title: 'setting.doctor_specialty',
            routerLink: 'setting/doctor-specialty',
            icon: 'person',
            isNoPermission:
              EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
                this.permissions?.includes(p),
              ).length === 0,
            roles: [],
          },
        ],
      },
    ];

    this.displayMenuItems = this.traverseMenuItems(menuItems);
  }

  onToggleMenu(): void {
    this.isMenuIcon = !this.isMenuIcon;
    this.util.menuChange.emit(this.isMenuIcon);
  }

  onCloseMenu(): void {
    this.isMenuIcon = true;
    this.util.menuChange.emit(this.isMenuIcon);
  }

  toggleExpendMenu(menu: MenuItem): void {
    menu.expanded = !menu.expanded;
    this._cd.detectChanges;
  }

  onMenuScroll(e: ScrollStatus): void {
    let scrolled = e.offset.y > 0;
    if (this.isScrolled != scrolled) {
      this.isScrolled = scrolled;
      this._cd.detectChanges();
    }
  }

  onClickMenu() {
    if (!this.isMenuIcon) {
      this.isMenuIcon = this.isHandset || this.isTablet;
    }
  }

  onLogout() {
    if (this.IS_ENABLE_OAUTH && this.singleSignOnService.isLoggedIn) {
      this.requestService.onCleanAuthInfo();
      this.authService.logOutEventEmitter.emit(true);
      this.singleSignOnService.onLogout();
    } else {
      this.authService.logout().subscribe((res: any) => {
        if (res.status === 1) {
          this.authService.logOutEventEmitter.emit(true);
          this.requestService.onSignOut();
        }
      });
    }
  }

  onScroll(status: ScrollStatus): void {
    let scrolled = status.offset.y > 200;
    if (this.isContentScrolled != scrolled) {
      this.isContentScrolled = scrolled;
      this._cd.detectChanges();
    }

    this.util.navigationScroll.emit(status);
  }

  onScrollLoaded(scroll: SmoothScrollbar): void {
    this.util.navigationScrollRef = scroll;
  }

  moveTop() {
    this.util.navigationScrollRef.scrollTo(0, 0, 350);
    this.util.navigationScrollRef.setMomentum(0, 1);
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }
}

export interface MenuItem {
  title: string; // Title to be displayed
  exact?: boolean; // Highlight when active or not
  routerLink?: string; // Link to redirect when click
  children?: MenuItem[]; // Children of the menu
  roles?: string[]; // Allowed roles for the menu
  icon?: string; // Icon to be displayed
  root?: string;
  expanded?: boolean; // Show children or not
  isNoPermission?: boolean;
  count?: number;
}
